import {
  Box,
  Button,
  constants,
  FieldMessage,
  Input,
  MinusIcon,
  PlusIcon,
  Stack,
} from '@redbubble/design-system';
import React, { useEffect, useState } from 'react';

import styles from './EditableQuantity.module.scss';

export interface EditableQuantityProps {
  label: string;
  name: string;
  subtractBtnAriaLabel: string;
  addBtnAriaLabel: string;
  defaultValue: number;
  minValue: number;
  maxValue?: number;
  onChange: (newValue: number) => void;
  disabled: boolean;
  errorMsg: string;
  disableAddButton?: boolean;
}

export const EditableQuantity = ({
  label,
  name,
  subtractBtnAriaLabel,
  addBtnAriaLabel,
  defaultValue,
  minValue,
  maxValue,
  onChange,
  disabled,
  errorMsg,
  disableAddButton,
}: EditableQuantityProps) => {
  const [value, setValue] = useState<number>(defaultValue);
  const [isEmpty, setIsEmpty] = useState(false);
  const [isLessThanMin, setIsLessThanMin] = useState(false);

  useEffect(() => {
    setValue(defaultValue);
  }, [defaultValue]);

  // TODO: BX-658 investigates validation with Formik
  const isInputBetweenRange = (newVal: string | number): boolean => {
    // if the value is between the min and max, then it's a valid value
    if (
      (minValue === undefined || Number(newVal) >= minValue) &&
      (maxValue === undefined || Number(newVal) <= maxValue)
    ) {
      return true;
    }

    return false;
  };

  const handleInputChange = (e: React.FormEvent<HTMLInputElement>) => {
    const { value: inputVal } = e.currentTarget;

    // check if the value is empty
    // since the input type is a number, we don't need to worry about white space
    if (typeof inputVal === 'string' && inputVal === '') {
      setIsEmpty(true);
      return;
    }

    setIsEmpty(false);
    setIsLessThanMin(false);

    const numVal = Number(inputVal);

    if (numVal < minValue) {
      setValue(numVal);
      setIsLessThanMin(true);
      return;
    }

    if (!isInputBetweenRange(inputVal)) return;

    setValue(numVal);

    if (numVal !== defaultValue) {
      onChange(numVal);
    }
  };

  const handleBlur = () => {
    if (isEmpty) {
      setValue(defaultValue);
    }

    // we still want to call onChange() if the user has input something that is less than
    // the minValue. So that we can show the 'remove confirmation button'
    if (isLessThanMin) {
      setValue(defaultValue);
      onChange(minValue - 1);
    }

    setIsEmpty(false);
    setIsLessThanMin(false);
  };

  const updateValueOnClick = (newVal: number) => {
    // if the new value is less than the minValue
    // we still want to trigger the onChange()
    // so that the 'Remove' confirmation btn is shown to the users
    if (newVal < minValue) {
      onChange(newVal);
      return;
    }

    if (!isInputBetweenRange(newVal)) return;

    setValue(newVal);
    onChange(newVal);
  };

  const handleSubtract = () => {
    updateValueOnClick(value - 1);
  };

  const handleAdd = () => {
    updateValueOnClick(value + 1);
  };

  return (
    <Stack direction={constants.VERTICAL} spacing="xxs" alignItems="center">
      <Stack direction={constants.HORIZONTAL} spacing="xxs" alignItems="center">
        <Button
          aria-label={subtractBtnAriaLabel}
          onClick={handleSubtract}
          size={constants.SMALL}
          iconBefore={
            // TODO: need to pass in aria-hidden when [DEP-1036] has been completed
            <MinusIcon size={constants.MEDIUM} />
          }
          circle
          strong
          disabled={disabled}
        />
        <Box className={styles.input}>
          {/* TODO[BX-658]: depending on the output of BX-658, we could be using the <Field /> component instead of <Input /> */}
          <Input
            aria-label={label}
            type="number"
            name={name}
            value={isEmpty ? '' : value}
            onChange={handleInputChange}
            onBlur={handleBlur}
            min={minValue}
            max={maxValue}
            pattern="\d*"
            fluid
            disabled={disabled}
            intent={errorMsg ? constants.ERROR : null}
            data-testid="line-item-editable-quantity-input"
          />
        </Box>
        <Button
          aria-label={addBtnAriaLabel}
          onClick={handleAdd}
          size={constants.SMALL}
          iconBefore={
            // TODO: need to pass in aria-hidden when [DEP-1036] has been completed
            <PlusIcon size={constants.MEDIUM} />
          }
          circle
          strong
          disabled={disabled || disableAddButton}
        />
      </Stack>
      {errorMsg ? (
        <FieldMessage intent={constants.ERROR}>{errorMsg}</FieldMessage>
      ) : null}
    </Stack>
  );
};
